import { useEffect, useRef } from 'react';

/**
 * Runs `callback` every `intervalMs` using a Web Worker timer instead of a
 * main-thread `setInterval` (MED-1260).
 *
 * Why a worker: Chrome throttles backgrounded-tab main-thread timers to ~1/min
 * ("intensive throttling"), which starves short-interval heartbeats when the
 * user backgrounds the tab (e.g. Medley behind Zoom during a live session). A
 * dedicated worker's timer is not throttled that way, so the heartbeat keeps
 * firing and server-side presence stays fresh. Where `Worker` is unavailable
 * (tests / SSR / very old browsers) it falls back to `window.setInterval` so
 * behavior degrades rather than breaks.
 *
 * The fallback covers two distinct failure modes (MED-1282 AC9): a *synchronous*
 * `new Worker(...)` throw (strict CSP / unsupported env), and an *asynchronous*
 * worker-load failure (a bad chunk URL after a deploy, or a CSP refusal at load
 * time) where construction succeeds but the module never loads, `onmessage`
 * never fires, and the timer would otherwise silently never tick — a worse
 * outage than the throttling this hook exists to fix. A `worker.onerror`
 * handler catches the async case and falls back to a main-thread interval.
 *
 * The latest `callback` is ref-latched so the worker is not torn down and
 * recreated when the callback identity changes between renders; the worker only
 * (re)starts when `intervalMs` or `enabled` changes.
 *
 * @param callback  Fired on each tick (runs on the main thread).
 * @param intervalMs  Tick period in milliseconds.
 * @param enabled  When false, no timer runs (default true).
 */
export function useWorkerInterval(
  callback: () => void,
  intervalMs: number,
  enabled = true,
): void {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return undefined;

    const tick = () => savedCallback.current();

    let worker: Worker | null = null;
    let fallbackId: number | null = null;
    let disposed = false;

    // Start a main-thread interval iff one isn't already running and the effect
    // hasn't been torn down. Idempotent so the sync-catch path and the async
    // onerror path can both call it without double-scheduling.
    const startFallback = () => {
      if (disposed || fallbackId !== null) return;
      fallbackId = window.setInterval(tick, intervalMs);
    };

    if (typeof Worker !== 'undefined') {
      try {
        worker = new Worker(new URL('../workers/intervalWorker.ts', import.meta.url), {
          type: 'module',
        });
        worker.onmessage = () => tick();
        // Async worker-load failure (bad chunk URL after a deploy, CSP refusal
        // at load): construction already succeeded, so the sync catch below
        // never runs — without this the timer would silently never tick
        // (MED-1282 AC9). Drop the dead worker and fall back to a main-thread
        // interval so a degraded-but-running timer is guaranteed.
        worker.onerror = () => {
          if (disposed) return;
          if (worker) {
            worker.terminate();
            worker = null;
          }
          startFallback();
        };
        worker.postMessage({ type: 'start', intervalMs });
      } catch {
        // Worker construction can throw under a strict CSP or unsupported env;
        // fall back to a main-thread interval below.
        worker = null;
      }
    }

    if (!worker) {
      startFallback();
    }

    return () => {
      disposed = true;
      if (worker) {
        worker.postMessage({ type: 'stop' });
        worker.terminate();
      }
      if (fallbackId !== null) {
        window.clearInterval(fallbackId);
      }
    };
  }, [intervalMs, enabled]);
}
